import { z } from "zod";
import { planView } from "./plan";
/**
 * The human trainer's surface (`D233`, `D269`–`D272`): the trainer's groups
 * and students, the invitations, a student's plan, workouts and weight, the
 * draft the trainer approves, and the trainer–student conversation.
 *
 * Everything sits under `/me/coaching`, because every read is "what I reach":
 * reach is derived from group membership on every call (`D50`), never carried
 * in a path or a token, and a student id in a path is only a question the
 * server answers against that reach.
 *
 * Online only. None of it goes through the outbox (`D163`); the one write a
 * resend could double, a chat message, carries the client's UUIDv7.
 */
export const COACHING_ROUTES = {
    /** `GET` — my groups (any role) and the invitations addressed to me. */
    hub: "/me/coaching",
    /** `POST` — a new group, the caller its owner (`D64`). */
    groups: "/me/coaching/groups",
    /** `POST` — accept one invitation addressed to my verified email (`D66`). */
    invitationAccept: "/me/coaching/invitations/:invitationId/accept",
    /** `POST` — end my own membership (`D65`: the student can always leave). */
    groupLeave: "/me/coaching/groups/:groupId/leave",
    /** `GET` — the group's students and its pending invitations (trainers). */
    roster: "/me/coaching/groups/:groupId/roster",
    /** `POST` — invite a student by email. */
    groupInvitations: "/me/coaching/groups/:groupId/invitations",
    /** `DELETE` — revoke a pending invitation. */
    groupInvitation: "/me/coaching/groups/:groupId/invitations/:invitationId",
    /** `GET` — one student's overview; `DELETE` — remove them from the group. */
    student: "/me/coaching/groups/:groupId/students/:accountId",
    /** `POST` — a template written into a new DRAFT for the student. */
    studentPlan: "/me/coaching/groups/:groupId/students/:accountId/plan",
    /** `DELETE` — discard the student's draft. */
    studentDraft: "/me/coaching/groups/:groupId/students/:accountId/draft",
    /** `POST` — publish the student's draft: the trainer's approval (`D270`). */
    studentDraftPublish: "/me/coaching/groups/:groupId/students/:accountId/draft/publish",
    /** `GET` the thread, `POST` one message (`D272`). */
    thread: "/me/coaching/groups/:groupId/threads/:studentId",
    /** `POST` — the reader's "seen up to now". */
    threadSeen: "/me/coaching/groups/:groupId/threads/:studentId/seen",
};
const segment = (value) => encodeURIComponent(value);
export function coachingGroupPath(kind, groupId) {
    return COACHING_ROUTES[kind].replace(":groupId", segment(groupId));
}
export function coachingInvitationAcceptPath(invitationId) {
    return COACHING_ROUTES.invitationAccept.replace(":invitationId", segment(invitationId));
}
export function coachingGroupInvitationPath(groupId, invitationId) {
    return COACHING_ROUTES.groupInvitation
        .replace(":groupId", segment(groupId))
        .replace(":invitationId", segment(invitationId));
}
export function coachingStudentPath(kind, groupId, accountId) {
    return COACHING_ROUTES[kind]
        .replace(":groupId", segment(groupId))
        .replace(":accountId", segment(accountId));
}
export function coachingThreadPath(kind, groupId, studentId) {
    return COACHING_ROUTES[kind]
        .replace(":groupId", segment(groupId))
        .replace(":studentId", segment(studentId));
}
/** A group's name, as the trainer types it. */
export const COACHING_GROUP_NAME_MAX = 60;
/**
 * Days without a finished workout after which a student is "sumido"
 * (`D271`). One number for the list's mark, the hub's count and the report.
 */
export const COACHING_INACTIVE_DAYS = 7;
/** `D64`'s ceiling: past it the trainer is warned, never blocked. */
export const COACHING_STUDENT_CEILING = 10;
/** One chat message (`D272`). */
export const COACHING_MESSAGE_MAX = 2000;
export const COACHING_ROLES = ["owner", "admin", "trainer", "student"];
export const coachingRole = z.enum(COACHING_ROLES);
export const coachingGroupRow = z.object({
    groupId: z.string().min(1),
    isOwner: z.boolean(),
    name: z.string(),
    /** Added by account and not yet accepted. */
    pending: z.boolean(),
    role: coachingRole,
    /** A student's unread messages from the group's staff (`D272`). */
    unread: z.number().int().min(0).optional(),
});
export const coachingInvitationRow = z.object({
    expiresAt: z.iso.datetime(),
    groupId: z.string().min(1),
    groupName: z.string(),
    invitationId: z.string().min(1),
    role: coachingRole,
});
export const coachingHubOutput = z.object({
    groups: z.array(coachingGroupRow),
    invitations: z.array(coachingInvitationRow),
});
export const coachingGroupCreateInput = z.strictObject({
    name: z.string().trim().min(1).max(COACHING_GROUP_NAME_MAX),
});
export const coachingGroupCreated = z.object({ groupId: z.string().min(1) });
export const coachingAcceptOutput = z.object({
    groupId: z.string().min(1),
    role: coachingRole,
});
export const coachingInviteInput = z.strictObject({
    email: z.string().trim().email().max(254),
});
export const coachingSentInvitation = z.object({
    email: z.string(),
    expiresAt: z.iso.datetime(),
    invitationId: z.string().min(1),
});
/** A person as the other side sees them: their chosen name, else the email. */
export const coachingPerson = z.object({
    accountId: z.string().min(1),
    /** `null` when the person never chose a name. */
    displayName: z.string().nullable(),
    /** `null` only when the account is gone. */
    email: z.string().nullable(),
});
export const coachingRosterStudent = coachingPerson.extend({
    /** `null` while nothing ended, or past the roster's probe cap. */
    lastWorkoutAt: z.iso.datetime().nullable(),
    memberSince: z.iso.datetime().nullable(),
    pending: z.boolean(),
    /** Messages from the student the reader has not seen (`D272`). */
    unread: z.number().int().min(0).optional(),
    workoutsLast30Days: z.number().int().min(0),
});
export const coachingRosterOutput = z.object({
    groupId: z.string().min(1),
    invitations: z.array(coachingSentInvitation),
    name: z.string(),
    role: coachingRole,
    students: z.array(coachingRosterStudent),
    truncated: z.boolean(),
});
export const coachingWorkoutRow = z.object({
    endedAt: z.iso.datetime().nullable(),
    exerciseCount: z.number().int().min(0),
    sessionId: z.string().min(1),
    setCount: z.number().int().min(0),
    startedAt: z.iso.datetime(),
    /** Σ load × reps over the sets that carry both, in kg. */
    volumeKg: z.number().min(0),
});
export const coachingWeightRow = z.object({
    measuredAt: z.iso.datetime(),
    unit: z.string(),
    value: z.number(),
});
export const coachingStudentOutput = z.object({
    /** Whether this trainer holds the pen on the student's active plan. */
    canWrite: z.boolean(),
    /** The draft waiting for approval — a template's or the agent's (`D270`). */
    draft: planView.nullable(),
    plan: planView.nullable(),
    student: coachingPerson.extend({
        memberSince: z.iso.datetime().nullable(),
    }),
    /** Ended and open sessions of the last 60 days, newest first. */
    workouts: z.array(coachingWorkoutRow),
    /** Body weight over the last 180 days, oldest first. */
    weight: z.array(coachingWeightRow),
});
export const coachingAssignInput = z.strictObject({
    templateId: z.string().min(1).max(64),
});
export const coachingDraftOutput = z.object({ planId: z.string().min(1) });
export const coachingMessage = z.object({
    body: z.string(),
    messageId: z.string().min(1),
    /** The reader wrote it. */
    mine: z.boolean(),
    sentAt: z.iso.datetime(),
});
export const coachingThreadOutput = z.object({
    /** Oldest first, the last 200. */
    messages: z.array(coachingMessage),
    /** The other side, as the reader sees them. */
    other: coachingPerson.nullable(),
});
export const coachingMessageInput = z.strictObject({
    body: z.string().trim().min(1).max(COACHING_MESSAGE_MAX),
    /** The client's UUIDv7, minted once per message: a resend is the same row. */
    id: z.string().uuid(),
});
//# sourceMappingURL=coaching.js.map